import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Wallet, IndianRupee, Pencil, RotateCcw, Check, X, MessageCircle } from "lucide-react";
import { waLink } from "@/lib/utils";
import StaffAccountDrawer from "@/components/StaffAccountDrawer";

// ---------- INR formatting helpers ---------------------------------------
// Standardised across both notify buttons. Templates use the spec-literal
// form `₹{value}` — sign is INSIDE the currency symbol (e.g. `₹-33,400`
// for a negative balance). `inrRaw` renders the signed value with Indian
// grouping. `inrAbs` renders the positive absolute value only (used for
// "Amount Paid" where paid amounts are conceptually always positive).
const inrRaw = (n) => {
  const num = Math.round(Number(n) || 0);
  return `₹${num.toLocaleString("en-IN")}`;
};
const inrAbs = (n) => {
  const num = Math.round(Number(n) || 0);
  return `₹${Math.abs(num).toLocaleString("en-IN")}`;
};

/**
 * Staff Shoot Balance Summary — admin-only auto-posted ledger.
 *
 * Iter32: TOTAL SHOOT EARNINGS = base_shoot_rate × shoot_count. Admin can
 * inline-edit the per-staff base_shoot_rate (defaults to ₹3,000). Manual
 * override still available — if set, overrides the computed base.
 */
export default function StaffLedger() {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rateBusy, setRateBusy] = useState({});
  const [assistBusy, setAssistBusy] = useState({});
  // iter158 — Manage Shoot Payments drawer target (staff row).
  const [shootPayTarget, setShootPayTarget] = useState(null);
  // iter189 — SHOOTS-column drill-down target.
  const [drilldown, setDrilldown] = useState(null);
  // iter196 — Staff Account drawer (opens on name click).
  const [accountStaffId, setAccountStaffId] = useState(null);
  useEffect(() => {
    // Bridge from the row-level SHOOTS button back into this component.
    window.__openShootsDrilldown = (row) => setDrilldown(row);
    return () => { delete window.__openShootsDrilldown; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/crew/ledger");
      setLedger(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load ledger");
      setLedger(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBaseRate = async (staffId, rate) => {
    setRateBusy((prev) => ({ ...prev, [staffId]: true }));
    try {
      await api.patch(`/team/staff/${staffId}`, { base_shoot_rate: rate });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save rate");
    } finally {
      setRateBusy((prev) => ({ ...prev, [staffId]: false }));
    }
  };

  const saveAssistRate = async (staffId, rate) => {
    setAssistBusy((prev) => ({ ...prev, [staffId]: true }));
    try {
      await api.patch(`/team/staff/${staffId}`, { assist_rate: rate });
      await load();
      toast.success("Assist rate updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save assist rate");
    } finally {
      setAssistBusy((prev) => ({ ...prev, [staffId]: false }));
    }
  };

  /**
   * Optimistically patch one row locally and recompute totals so the UI
   * feels instant. Server confirmation comes via the PATCH response; on
   * error we roll back by reloading.
   */
  const applyLocalPatch = (staffId, patch) => {
    setLedger((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r) => {
        if (r.staff_id !== staffId) return r;
        const next = { ...r, ...patch };
        // iter198.4 — Recompute both `total_earnings` (lifetime, all frozen)
        // and `current_earnings` (frozen with event_date <= today). Server
        // formula: outstanding = current_earnings + adjust. Upcoming stays
        // untouched by manual overrides — it's a factual future amount.
        const upcoming = Number(next.upcoming_earnings || 0);
        const autoCurrent = Math.max(0, Number(next.total_earnings_auto || 0) - upcoming);
        if (next.total_earnings_override != null) {
          const base = Number(next.total_earnings_override);
          next.total_earnings = base + upcoming;   // lifetime = override + upcoming
          next.current_earnings = base;             // override maps to current
        } else {
          next.total_earnings = Number(next.total_earnings_auto || 0);
          next.current_earnings = autoCurrent;
        }
        next.outstanding_due = Math.round(
          (Number(next.current_earnings || 0) + Number(next.adjust_amount || 0)) * 100
        ) / 100;
        return next;
      });
      const totals = {
        total_earnings: rows.reduce((s, r) => s + Number(r.total_earnings || 0), 0),
        adjust_amount: Math.round(rows.reduce((s, r) => s + Number(r.adjust_amount || 0), 0) * 100) / 100,
        outstanding_due: Math.round(rows.reduce((s, r) => s + Number(r.outstanding_due || 0), 0) * 100) / 100,
        current_earnings: Math.round(rows.reduce((s, r) => s + Number(r.current_earnings || 0), 0) * 100) / 100,
        upcoming_earnings: Math.round(rows.reduce((s, r) => s + Number(r.upcoming_earnings || 0), 0) * 100) / 100,
      };
      return { ...prev, rows, totals };
    });
  };

  const patchLedger = async (staffId, body) => {
    try {
      const { data } = await api.patch(`/team/crew/ledger/${staffId}`, body);
      // Server may return the canonical values — reconcile locally.
      applyLocalPatch(staffId, {
        adjust_amount: Number(data.adjust_amount || 0),
        total_earnings_override:
          data.total_earnings_override === undefined ? null : data.total_earnings_override,
      });
      return { ok: true, data };
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
      load();
      return { ok: false };
    }
  };

  const rows = ledger?.rows || [];
  const totals = ledger?.totals || { total_earnings: 0, adjust_amount: 0, outstanding_due: 0 };
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <div className="card overflow-hidden" data-testid="staff-ledger-summary">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-rose-50 to-white border-b border-stone-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-800 shrink-0">
            <Wallet className="w-4 h-4" strokeWidth={1.8} />
          </div>
          <div>
            <h2 className="font-heading text-base font-semibold">Staff Shoot Balance Summary</h2>
            <div className="text-[11px] text-stone-500">
              Click the pencil to override auto-calculated shoot earnings. Type a manual adjustment (± ₹) to record payouts. Admin-only.
            </div>
          </div>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-stone-400" />}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="ledger-table">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold text-left">
              <th className="px-3 py-2 w-40">Team member</th>
              <th className="px-3 py-2 w-32">Rate / shoot</th>
              <th className="px-3 py-2 w-32">Assist rate</th>
              <th className="px-3 py-2 text-right w-52">Total shoot earnings</th>
              <th className="px-3 py-2 w-40">Manual ledger adjust (±)</th>
              <th className="px-3 py-2 text-right w-44">Final outstanding due</th>
              <th className="px-3 py-2 w-16 text-right" title="Number of shoots credited">Shoots</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((r) => (
              <LedgerRow
                key={r.staff_id}
                row={r}
                onSave={(patch) => patchLedger(r.staff_id, patch)}
                onSaveRate={(rate) => saveBaseRate(r.staff_id, rate)}
                onSaveAssist={(rate) => saveAssistRate(r.staff_id, rate)}
                onManageShootPayments={(staffRow) => setShootPayTarget(staffRow)}
                onQuickPay={async (amount) => {
                  try {
                    const { data } = await api.post(`/team/crew/ledger/${r.staff_id}/quick-pay`, { amount });
                    applyLocalPatch(r.staff_id, {
                      adjust_amount: Number(data.adjust_amount || 0),
                    });
                    return data;
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Quick pay failed");
                    return null;
                  }
                }}
                rateBusy={!!rateBusy[r.staff_id]}
                assistBusy={!!assistBusy[r.staff_id]}
                inr={inr}
                onOpenAccount={(sid) => setAccountStaffId(sid)}
              />
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-stone-400 text-xs">No active staff yet.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-stone-50 border-t-2 border-stone-200">
              <tr className="font-semibold" data-testid="ledger-totals">
                <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-stone-600">Totals</td>
                <td />
                <td />
                <td className="px-3 py-2 text-right tabular-nums" data-testid="ledger-totals-earnings">{inr(totals.total_earnings)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${totals.adjust_amount < 0 ? "text-red-700" : "text-stone-700"}`} data-testid="ledger-totals-adjust">
                  {totals.adjust_amount > 0 ? "+" : ""}{inr(totals.adjust_amount)}
                </td>
                <td className={`px-3 py-2 text-right tabular-nums font-heading text-base ${totals.outstanding_due > 0 ? "text-rose-800" : "text-stone-500"}`} data-testid="ledger-totals-outstanding">
                  {inr(totals.outstanding_due)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {shootPayTarget && (
        <ShootPaymentsDrawer
          staff={shootPayTarget}
          onClose={() => setShootPayTarget(null)}
          onChanged={load}
        />
      )}
      {drilldown && (
        <ShootsDrilldownModal
          staff={drilldown}
          onClose={() => setDrilldown(null)}
          inr={inr}
        />
      )}

      {/* iter196 — Staff Account drawer (opens on name click) */}
      <StaffAccountDrawer
        open={!!accountStaffId}
        staffId={accountStaffId}
        onClose={() => setAccountStaffId(null)}
        onLedgerRefresh={load}
      />
    </div>
  );
}

// ---------- iter189: SHOOTS drill-down modal -------------------------------
function ShootsDrilldownModal({ staff, onClose, inr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/team/crew/ledger/${staff.staff_id}/shoots`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || "Could not load shoots");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [staff.staff_id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="shoots-drilldown-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold">Shoots included in ledger</div>
            <div className="font-heading text-lg text-stone-900">{staff.name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-900" data-testid="shoots-drilldown-close">✕</button>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-stone-500">Loading…</div>
        ) : !data || data.count === 0 ? (
          <div className="p-6 text-sm text-stone-500">No frozen shoots yet.</div>
        ) : (
          <>
            <table className="min-w-full text-sm" data-testid="shoots-drilldown-table">
              <thead className="bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Event / Couple</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {data.shoots.map((s) => (
                  <tr key={s.id} className="border-b border-stone-100" data-testid={`shoots-drilldown-row-${s.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-stone-700">{s.date || "—"}</td>
                    <td className="px-3 py-2 text-stone-800">{(s.couple_name || "") + (s.event ? ` · ${s.event}` : "")}</td>
                    <td className="px-3 py-2 text-stone-600">{s.job_role || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-mono text-stone-900">{inr(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-stone-50 border-t-2 border-stone-200 font-semibold">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-[11px] uppercase tracking-wider text-stone-600">
                    Total shoot earnings · {data.count} shoots
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-heading" data-testid="shoots-drilldown-total">
                    {inr(data.total_earnings)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Row ----------------------------------------------------------

function LedgerRow({ row, onSave, onSaveRate, onSaveAssist, onQuickPay, rateBusy, assistBusy, inr, onManageShootPayments, onOpenAccount }) {
  const isOwner = !!useAuth().user?.is_owner;
  // iter96 — The adjust input is now a DIRECT HANDLE on the outstanding
  // balance so the number in the input box ALWAYS matches the "Final
  // Outstanding" cell to the right. Persistence layer is unchanged:
  //   input value  = target outstanding (X)
  //   new_adjust   = X − base_earnings
  //   server keeps outstanding = base_earnings + adjust  ✓
  // Previously the input stored `adjust_amount` (a raw delta) which
  // diverged from `outstanding_due` whenever the staff had any base
  // earnings or a prior Quick Pay had shifted adjust_amount.
  const outstandingStr = (v) => (Number(v || 0) === 0 ? "" : String(v));
  const [adjustDraft, setAdjustDraft] = useState(outstandingStr(row.outstanding_due));
  const [editingEarnings, setEditingEarnings] = useState(false);
  const [earningsDraft, setEarningsDraft] = useState(String(row.total_earnings || 0));
  const [rateDraft, setRateDraft] = useState(String(row.base_shoot_rate ?? 3000));
  const [assistDraft, setAssistDraft] = useState(String(row.assist_rate ?? 1200));

  useEffect(() => {
    setAdjustDraft(outstandingStr(row.outstanding_due));
  }, [row.outstanding_due]);
  useEffect(() => {
    if (!editingEarnings) setEarningsDraft(String(row.total_earnings || 0));
  }, [row.total_earnings, editingEarnings]);
  useEffect(() => {
    setRateDraft(String(row.base_shoot_rate ?? 3000));
  }, [row.base_shoot_rate]);
  useEffect(() => {
    setAssistDraft(String(row.assist_rate ?? 1200));
  }, [row.assist_rate]);

  const commitRate = () => {
    const parsed = Number(rateDraft);
    if (Number.isNaN(parsed) || parsed < 0) {
      setRateDraft(String(row.base_shoot_rate ?? 3000));
      return;
    }
    if (parsed !== Number(row.base_shoot_rate ?? 3000)) onSaveRate(parsed);
  };

  const commitAssist = () => {
    const parsed = Number(assistDraft);
    if (Number.isNaN(parsed) || parsed < 0) {
      setAssistDraft(String(row.assist_rate ?? 1200));
      return;
    }
    if (parsed !== Number(row.assist_rate ?? 1200)) onSaveAssist(parsed);
  };

  const commitAdjust = () => {
    // iter96 — Interpret the input value as the DESIRED outstanding
    // balance (X). Send `adjust_amount = X − current_earnings` so the
    // server's `outstanding = current_earnings + adjust` recomputation
    // lands on exactly X. Rounding to 2 dp mirrors the backend.
    // iter198.4 — baseline is CURRENT earnings (excludes upcoming shoots)
    // to match the server-side outstanding formula.
    const trimmed = adjustDraft.trim();
    const parsed = trimmed === "" ? 0 : Number(trimmed);
    if (Number.isNaN(parsed)) {
      setAdjustDraft(outstandingStr(row.outstanding_due));
      return;
    }
    const baseEarnings = Number(row.current_earnings ?? row.total_earnings ?? 0);
    const desiredOutstanding = parsed;
    const currentOutstanding = Math.round(Number(row.outstanding_due || 0) * 100) / 100;
    if (desiredOutstanding === currentOutstanding) return;
    const newAdjust = Math.round((desiredOutstanding - baseEarnings) * 100) / 100;
    onSave({ adjust_amount: newAdjust });
  };

  const commitEarnings = () => {
    const parsed = Number(earningsDraft);
    if (Number.isNaN(parsed)) {
      setEarningsDraft(String(row.total_earnings || 0));
      setEditingEarnings(false);
      return;
    }
    setEditingEarnings(false);
    if (parsed !== Number(row.total_earnings || 0)) {
      onSave({ total_earnings_override: parsed });
    }
  };

  const clearOverride = () => {
    // Send explicit null so backend $unsets the override.
    onSave({ total_earnings_override: null });
  };

  const dueColor = row.outstanding_due > 0
    ? "text-rose-800"
    : row.outstanding_due < 0
      ? "text-red-700"
      : "text-stone-400";

  const isOverridden = row.total_earnings_override != null;

  return (
    <tr data-testid={`ledger-row-${row.staff_id}`}>
      <td className="px-3 py-2">
        <button
          type="button"
          className="font-semibold text-stone-800 hover:text-emerald-800 hover:underline underline-offset-2 decoration-emerald-700/40 text-left transition"
          onClick={() => onOpenAccount?.(row.staff_id)}
          data-testid={`ledger-open-account-${row.staff_id}`}
          title="Open Staff Account statement"
        >
          {row.name}
        </button>
      </td>

      {/* Iter32 — per-staff editable base_shoot_rate (default ₹3,000). */}
      <td className="px-3 py-2" data-testid={`ledger-rate-cell-${row.staff_id}`}>
        <div className="inline-flex items-center gap-1">
          <span className="text-stone-400 text-xs">₹</span>
          <input
            type="number"
            step="any"
            min="0"
            className="input py-1 text-xs w-20 text-right font-mono"
            value={rateDraft}
            disabled={rateBusy}
            onChange={(e) => setRateDraft(e.target.value)}
            onBlur={commitRate}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={`ledger-rate-input-${row.staff_id}`}
          />
          <span className="text-[9px] text-stone-400">/shoot</span>
          <button
            type="button"
            onClick={() => onManageShootPayments?.(row)}
            className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 hover:text-rose-900 hover:bg-rose-50 rounded px-1.5 py-0.5 border border-transparent hover:border-rose-200 transition"
            title="Manage per-booking payment overrides for this staff"
            data-testid={`ledger-manage-shoot-payments-${row.staff_id}`}
          >
            Manage
          </button>
        </div>
      </td>

      {/* iter190 — per-staff editable assist_rate (default ₹1,200). */}
      <td className="px-3 py-2" data-testid={`ledger-assist-rate-cell-${row.staff_id}`}>
        <div className="inline-flex items-center gap-1">
          <span className="text-stone-400 text-xs">₹</span>
          <input
            type="number"
            step="any"
            min="0"
            className="input py-1 text-xs w-20 text-right font-mono"
            value={assistDraft}
            disabled={assistBusy}
            onChange={(e) => setAssistDraft(e.target.value)}
            onBlur={commitAssist}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            title="Used when job role = ASSIST. Defaults to ₹1,200."
            data-testid={`ledger-assist-rate-input-${row.staff_id}`}
          />
          <span className="text-[9px] text-stone-400">/assist</span>
        </div>
      </td>

      {/* Total shoot earnings — click pencil to override */}
      <td className="px-3 py-2 text-right" data-testid={`ledger-earnings-cell-${row.staff_id}`}>
        {editingEarnings ? (
          <div className="inline-flex items-center gap-1">
            <span className="text-stone-400 text-xs">₹</span>
            <input
              autoFocus
              type="number"
              step="any"
              className="input py-1 text-xs w-28 text-right font-mono"
              value={earningsDraft}
              onChange={(e) => setEarningsDraft(e.target.value)}
              onBlur={commitEarnings}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") { setEditingEarnings(false); setEarningsDraft(String(row.total_earnings || 0)); }
              }}
              data-testid={`ledger-earnings-input-${row.staff_id}`}
            />
            <button
              type="button"
              onClick={commitEarnings}
              className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
              title="Save"
              data-testid={`ledger-earnings-save-${row.staff_id}`}
            >
              <Check className="w-3.5 h-3.5" strokeWidth={2.4} />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setEditingEarnings(false); setEarningsDraft(String(row.total_earnings || 0)); }}
              className="p-1 rounded hover:bg-stone-100 text-stone-500"
              title="Cancel"
              data-testid={`ledger-earnings-cancel-${row.staff_id}`}
            >
              <X className="w-3.5 h-3.5" strokeWidth={2.4} />
            </button>
          </div>
        ) : (
          <div className="inline-flex flex-col items-end gap-0.5">
            <div className="inline-flex items-center gap-2 justify-end">
              {isOverridden && (
                <span
                  className="text-[9px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
                  title={`Auto sum was ${inr(row.total_earnings_auto)}`}
                  data-testid={`ledger-earnings-override-badge-${row.staff_id}`}
                >
                  Override
                </span>
              )}
              <span
                className={`tabular-nums font-mono ${isOverridden ? "text-amber-900 font-semibold" : "text-emerald-800"}`}
                title={
                  Number(row.upcoming_earnings || 0) > 0
                    ? `Current payable ${inr(row.current_earnings || 0)} + Upcoming ${inr(row.upcoming_earnings || 0)}`
                    : undefined
                }
                data-testid={`ledger-earnings-${row.staff_id}`}
              >
                {inr(row.total_earnings)}
              </span>
              <button
                type="button"
                onClick={() => { setEarningsDraft(String(row.total_earnings || 0)); setEditingEarnings(true); }}
                className="p-1 rounded hover:bg-rose-50 text-stone-400 hover:text-rose-700"
                title="Override auto-calculated total"
                data-testid={`ledger-earnings-edit-${row.staff_id}`}
              >
                <Pencil className="w-3 h-3" strokeWidth={2.2} />
              </button>
              {isOverridden && (
                <button
                  type="button"
                  onClick={clearOverride}
                  className="p-1 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-700"
                  title={`Revert to auto sum (${inr(row.total_earnings_auto)})`}
                  data-testid={`ledger-earnings-revert-${row.staff_id}`}
                >
                  <RotateCcw className="w-3 h-3" strokeWidth={2.2} />
                </button>
              )}
            </div>
            {Number(row.upcoming_earnings || 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
                title={`${row.upcoming_count} upcoming shoot${row.upcoming_count === 1 ? "" : "s"} not yet due`}
                data-testid={`ledger-upcoming-${row.staff_id}`}
              >
                +{inr(row.upcoming_earnings || 0)} upcoming
              </span>
            )}
          </div>
        )}
      </td>

      {/* Manual adjustment + Quick Pay (iter46) */}
      <td className="px-3 py-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <span className="text-stone-400 text-xs">₹</span>
            <input
              type="number"
              step="any"
              className="input py-1 text-xs w-full text-right font-mono"
              value={adjustDraft}
              onChange={(e) => setAdjustDraft(e.target.value)}
              onBlur={commitAdjust}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              placeholder="0"
              data-testid={`ledger-adjust-input-${row.staff_id}`}
            />
            <LedgerWhatsappButton
              row={row}
              adjustDraft={adjustDraft}
              onCommit={async (targetOutstanding) => {
                // iter96 — Input value is the TARGET outstanding balance.
                // Translate to raw adjust_amount for the PATCH so the
                // server's `outstanding = base + adjust` recomputation
                // lands on exactly the value the admin typed.
                const baseEarnings = Number(row.total_earnings || 0);
                const currentOutstanding = Math.round(Number(row.outstanding_due || 0) * 100) / 100;
                if (targetOutstanding === currentOutstanding) return true;
                const newAdjust = Math.round((targetOutstanding - baseEarnings) * 100) / 100;
                const res = await onSave({ adjust_amount: newAdjust });
                return !!res?.ok;
              }}
            />
          </div>
          {isOwner && <QuickPayInput row={row} onQuickPay={onQuickPay} />}
        </div>
      </td>

      <td className={`px-3 py-2 text-right tabular-nums font-heading font-semibold ${dueColor}`} data-testid={`ledger-due-${row.staff_id}`}>
        <IndianRupee className="w-3 h-3 inline -mt-0.5" strokeWidth={2} />
        {Number(row.outstanding_due || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </td>
      <td className="px-3 py-2 text-right text-xs tabular-nums">
        {row.shoot_count > 0 ? (
          <button
            type="button"
            onClick={() => (window.__openShootsDrilldown || (() => {}))(row)}
            className="text-rose-700 hover:text-rose-900 underline decoration-dotted underline-offset-2 font-semibold"
            data-testid={`ledger-shoots-drill-${row.staff_id}`}
            title="Click to see the exact shoots included in this ledger balance"
          >
            {row.shoot_count}
          </button>
        ) : (
          <span className="text-stone-400">0</span>
        )}
      </td>
    </tr>
  );
}

// ---------- WhatsApp trigger for ledger adjustment --------------------------
// Iter35 — ATOMIC "one-click": clicking the icon first commits the currently
// typed `adjustDraft` via the parent's `onCommit(amount)` (which awaits
// PATCH /team/crew/ledger/{staff_id}) and only opens the wa.me link after
// the save succeeds. Prevents the mismatch case where the admin notifies a
// value that isn't yet persisted.
function LedgerWhatsappButton({ row, adjustDraft, onCommit }) {
  const [busy, setBusy] = useState(false);

  // iter96 — `adjustDraft` is now the TARGET outstanding balance (not
  // the raw adjust_amount delta). Projection re-derives previous vs
  // updated balance directly from that so the WhatsApp message shows
  // exactly what the input box shows.
  //   • previousBalance = row.outstanding_due (server ground truth)
  //   • updatedBalance  = user's typed value (or current when blank)
  //   • delta           = updatedBalance − previousBalance
  //   • newAdjust       = updatedBalance − baseEarnings  (for PATCH)
  const projection = useMemo(() => {
    const trimmed = (adjustDraft ?? "").toString().trim();
    const parsedDraft = trimmed === "" ? NaN : Number(trimmed);
    const hasDraftAmount = !Number.isNaN(parsedDraft);
    const baseEarnings = Number(row.total_earnings || 0);
    const previousBalance = Math.round(Number(row.outstanding_due || 0) * 100) / 100;
    const updatedBalance = hasDraftAmount
      ? Math.round(parsedDraft * 100) / 100
      : previousBalance;
    const delta = Math.round((updatedBalance - previousBalance) * 100) / 100;
    const newAdjust = Math.round((updatedBalance - baseEarnings) * 100) / 100;
    return { newAdjust, previousBalance, updatedBalance, delta, hasDraftAmount };
  }, [adjustDraft, row.outstanding_due, row.total_earnings]);

  const { newAdjust, previousBalance, updatedBalance, delta } = projection;
  const disabled = updatedBalance === previousBalance || !row.whatsapp || busy;

  const handleClick = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      // iter96 — Commit the TARGET outstanding (`updatedBalance`).
      // The parent translates it to `adjust_amount = target − base`
      // before PATCH so the server's recompute lands on the exact
      // value shown in both the input box AND the Final Outstanding cell.
      const ok = await onCommit(updatedBalance);
      if (!ok) return;                       // save failed → toast already fired
      const name = row.name || "Team member";
      // Strict multiline layout — matches Kahani studio standard:
      //   Header · 📊 Breakdown · Previous Balance · Amount Paid/Adjusted · Total Updated Balance · Signature
      const isCredit = delta > 0;
      const header = isCredit
        ? `Hello ${name}, an incentive/credit of ${inrAbs(delta)} has been added to your Kahani studio ledger.`
        : `Hello ${name}, an advance/debit of ${inrAbs(delta)} has been recorded on your Kahani studio ledger.`;
      const message =
        `${header}\n\n` +
        `📊 Breakdown:\n` +
        `• Previous Balance: ${inrRaw(previousBalance)}\n` +
        `• Amount ${isCredit ? "Credited" : "Adjusted"}: ${isCredit ? "+" : "-"}${inrAbs(delta)}\n` +
        `• Total Updated Balance: ${inrRaw(updatedBalance)}\n\n` +
        `Thank you, Kahani Studio.`;
      toast.success(`Saved · Opening WhatsApp for ${name}…`);
      window.open(waLink(row.whatsapp, message), "_blank", "noopener,noreferrer");
    } finally {
      setBusy(false);
    }
  };

  const title = !row.whatsapp
    ? "No WhatsApp number on file for this staff"
    : disabled && !busy
      ? "Type an adjustment amount that differs from the current saved value"
      : busy
        ? "Saving…"
        : delta < 0
          ? `Save & notify ${row.name} — advance/debit of ${inrAbs(delta)}`
          : `Save & notify ${row.name} — incentive/credit of ${inrAbs(delta)}`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`p-1.5 rounded-md shrink-0 transition ${
        disabled
          ? "text-stone-300 cursor-not-allowed"
          : delta < 0
            ? "text-red-700 hover:bg-red-50"
            : "text-emerald-700 hover:bg-emerald-50"
      }`}
      title={title}
      aria-label={title}
      data-testid={`ledger-notify-btn-${row.staff_id}`}
    >
      {busy
        ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
        : <MessageCircle className="w-4 h-4" strokeWidth={2} />}
    </button>
  );
}


// ---------- Quick Pay input (iter46) ----------------------------------------
// Numeric field + submit checkmark below the WhatsApp button. On submit:
//   1. calls parent's onQuickPay(amount) which hits POST
//      /team/crew/ledger/{staff}/quick-pay
//   2. server atomically decrements adjust_amount and returns
//      { staff_name, whatsapp, previous_balance, new_balance, amount_paid }
//   3. clears the input, then opens a wa.me deep-link with the exact
//      breakdown template requested by the studio owner
function QuickPayInput({ row, onQuickPay }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const trimmed = amount.toString().trim();
  const parsed = Number(trimmed);
  const canSubmit = trimmed !== "" && Number.isFinite(parsed) && parsed > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const data = await onQuickPay(parsed);
      if (!data) return;
      toast.success(`₹${parsed.toLocaleString("en-IN")} recorded · ${row.name}`);
      setAmount("");
      // Fire WhatsApp only if the staff has a number on file.
      if (data.whatsapp) {
        // Defensive Number() casts — server already returns floats, but this
        // guarantees arithmetic-clean values even if a proxy ever mangles them
        // into strings. Guards against the "-33400" + "5000" = "-334005000"
        // string-concat class of bug.
        const prev = Number(data.previous_balance);
        const paid = Number(data.amount_paid);
        const next = Number(data.new_balance);
        const msg =
          `Hello ${data.staff_name || row.name}, a payment of ${inrAbs(paid)} has been recorded in your ledger.\n\n` +
          `📊 Breakdown:\n` +
          `• Previous Balance: ${inrRaw(prev)}\n` +
          `• Amount Paid: ${inrAbs(paid)}\n` +
          `• Total Updated Balance: ${inrRaw(next)}\n\n` +
          `Thank you, Kahani Studio.`;
        const url = waLink(data.whatsapp, msg);
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1" data-testid={`ledger-quickpay-row-${row.staff_id}`}>
      <input
        type="number"
        min="0"
        step="any"
        placeholder="Pay amount…"
        className="input py-1 text-xs w-full text-right font-mono"
        value={amount}
        disabled={busy}
        onChange={(e) => setAmount(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        data-testid={`ledger-quickpay-input-${row.staff_id}`}
      />
      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        className={`p-1 rounded transition ${canSubmit ? "text-emerald-700 hover:bg-emerald-50" : "text-stone-300 cursor-not-allowed"}`}
        title={canSubmit ? `Record payment of ₹${parsed.toLocaleString("en-IN")} and notify ${row.name} on WhatsApp` : "Enter a positive amount"}
        aria-label="Submit quick pay"
        data-testid={`ledger-quickpay-submit-${row.staff_id}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Check className="w-4 h-4" strokeWidth={2.5} />}
      </button>
    </div>
  );
}



// ---------- iter158 — Manage Shoot Payments drawer -------------------------
function ShootPaymentsDrawer({ staff, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/staff/${staff.staff_id}/shoot-payments`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load shoot payments");
    } finally { setLoading(false); }
  }, [staff.staff_id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => {
      const hay = `${r.client_name} ${r.event} ${r.place} ${r.job_role}`.toLowerCase();
      return hay.includes(t);
    });
  }, [rows, q]);

  const setOverride = async (row, actual, reason = "") => {
    setBusyId(row.id);
    try {
      const body = actual === "" || actual == null
        ? { amount: null }
        : { amount: Number(actual), reason };
      const { data } = await api.patch(`/team/crew/${row.id}/rate-override`, body);
      setRows((prev) => prev.map((r) => r.id === row.id ? {
        ...r,
        actual_rate: data.actual_rate,
        override_amount: data.override_amount,
        override_reason: data.override_reason || "",
        status: data.status,
      } : r));
      onChanged?.();
      toast.success(body.amount == null ? "Reverted to default rate" : `Rate updated to ₹${Number(body.amount).toLocaleString("en-IN")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save rate");
    } finally { setBusyId(null); }
  };

  const inrFmt = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="shoot-payments-drawer"
    >
      <div className="w-full max-w-4xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Financials &amp; Payroll · Overrides</div>
            <h2 className="font-heading text-xl font-semibold text-stone-900 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-rose-700" strokeWidth={1.8} /> Manage Shoot Payments
            </h2>
            <div className="text-xs text-stone-500 mt-1">
              <span className="font-semibold text-stone-800" data-testid="shoot-payments-staff-name">{staff.name}</span>
              <span className="mx-1.5">·</span>
              Default <span className="font-mono font-semibold">{inrFmt(staff.base_shoot_rate)}</span>
              <span className="mx-1.5">·</span>
              {rows.length} booking{rows.length === 1 ? "" : "s"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-stone-100" aria-label="Close" data-testid="shoot-payments-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-3 border-b border-stone-100 bg-stone-50/40">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by client, event, venue or role…"
            className="input w-full text-sm"
            data-testid="shoot-payments-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-stone-400 py-10"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-stone-400 py-16" data-testid="shoot-payments-empty">
              <Wallet className="w-10 h-10 mx-auto mb-3 text-stone-300" strokeWidth={1.2} />
              {rows.length === 0 ? "No shoots assigned to this crew member yet." : `No shoots match "${q}".`}
            </div>
          ) : (
            <div className="rounded-xl border border-stone-200 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold text-left">
                    <th className="px-3 py-2">Booking</th>
                    <th className="px-3 py-2 w-28">Event Date</th>
                    <th className="px-3 py-2 w-24">Role</th>
                    <th className="px-3 py-2 w-28 text-right">Default Rate</th>
                    <th className="px-3 py-2 w-40 text-right">Actual Paid Rate</th>
                    <th className="px-3 py-2 w-32">Status</th>
                    <th className="px-3 py-2 w-16 text-right">Reset</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filtered.map((r) => (
                    <ShootPaymentRow
                      key={r.id}
                      row={r}
                      busy={busyId === r.id}
                      onSave={(amount, reason) => setOverride(r, amount, reason)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-stone-200 text-[11px] text-stone-500 bg-white sticky bottom-0">
          Overrides apply to a <span className="font-semibold text-stone-700">single booking only</span>.
          The staff's default rate never changes. All changes are audited.
        </div>
      </div>
    </div>
  );
}

function ShootPaymentRow({ row, busy, onSave }) {
  const [draft, setDraft] = useState(String(row.actual_rate ?? row.default_rate));
  useEffect(() => {
    setDraft(String(row.actual_rate ?? row.default_rate));
  }, [row.actual_rate, row.default_rate]);

  const commit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 0) return;
    if (n === Number(row.actual_rate)) return;
    onSave(n, "");
  };

  const isCustom = row.status === "custom";
  return (
    <tr data-testid={`shoot-payments-row-${row.id}`}>
      <td className="px-3 py-2">
        <div className="font-medium text-stone-800" data-testid={`shoot-payments-client-${row.id}`}>{row.client_name || "—"}</div>
        <div className="text-[10px] text-stone-500 mt-0.5">{row.event}{row.place ? ` · ${row.place}` : ""}</div>
      </td>
      <td className="px-3 py-2 font-mono text-xs text-stone-600 whitespace-nowrap">{row.event_date || "—"}</td>
      <td className="px-3 py-2 text-xs text-stone-600 uppercase tracking-wider">{row.job_role || "—"}</td>
      <td className="px-3 py-2 text-right font-mono text-xs text-stone-500">₹{Math.round(row.default_rate).toLocaleString("en-IN")}</td>
      <td className="px-3 py-2">
        <div className="inline-flex items-center gap-1 justify-end w-full">
          <span className="text-stone-400 text-xs">₹</span>
          <input
            type="number"
            step="any"
            min="0"
            value={draft}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            className="input py-1 text-xs w-24 text-right font-mono"
            data-testid={`shoot-payments-actual-${row.id}`}
          />
        </div>
      </td>
      <td className="px-3 py-2">
        {isCustom ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider font-bold" data-testid={`shoot-payments-status-${row.id}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Custom
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-wider font-bold" data-testid={`shoot-payments-status-${row.id}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Default
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-right">
        {isCustom ? (
          <button
            type="button"
            onClick={() => onSave("", "")}
            disabled={busy}
            className="text-[10px] text-stone-500 hover:text-rose-700 underline underline-offset-2"
            title="Revert to default rate"
            data-testid={`shoot-payments-reset-${row.id}`}
          >
            Reset
          </button>
        ) : (
          <span className="text-stone-300 text-[10px]">—</span>
        )}
      </td>
    </tr>
  );
}
